import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { ReturnStruct } from '../../structs/return.struct';
import { RegraTributariaNaturezaOperacaoStruct } from '../../structs/motor-fiscal/regra-tributaria-natureza-operacao.struct';
import { ListaRegrasTributariasResponse } from '../../responses/motor-fiscal/lista-regras-tributarias.response';
import { RegraTributariaLookupResponse } from '../../responses/motor-fiscal/regra-tributaria-lookup.response';
import { RegraTributariaCompletaRequest } from '../../requests/motor-fiscal/regra-tributaria-completa.request';
import { RegraTributariaBulkDeleteRequest } from '../../requests/motor-fiscal/regra-tributaria-bulk-delete.request';
import { RegraTributariaCompletaResponse } from '../../responses/motor-fiscal/regra-tributaria-completa.response';
import { RegraTributariaSalvarResponse } from '../../responses/motor-fiscal/regra-tributaria-salvar.response';
import { RegraNaturezasVinculadasResponse } from '../../responses/motor-fiscal/regra-naturezas-vinculadas.response';
import { RegraTributariaSobreposicaoResponse } from '../../responses/motor-fiscal/regra-tributaria-sobreposicao.response';

@Injectable({ providedIn: 'root' })
export class RegraTributariaService extends CommonService {
    private readonly apiUrl: string = environment.urlApiMotorFiscal + 'RegraTributaria';

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getAll(
        pesquisa: string | null,
        codigoModeloDocumento: string | null,
        idNaturezaOperacao: number | null,
        paginaAtual: number,
        tamanhoPagina: number
    ): Observable<ListaRegrasTributariasResponse> {
        let params = new HttpParams()
            .set('paginaAtual', paginaAtual.toString())
            .set('tamanhoPagina', tamanhoPagina.toString());

        if (pesquisa) {
            params = params.set('pesquisa', pesquisa);
        }
        if (codigoModeloDocumento != null) {
            params = params.set('codigoModeloDocumento', codigoModeloDocumento);
        }
        if (idNaturezaOperacao != null) {
            params = params.set('idNaturezaOperacao', idNaturezaOperacao.toString());
        }

        return this.httpClient.get<ListaRegrasTributariasResponse>(`${this.apiUrl}/all`, {
            params,
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    /**
     * Regras ativas para o seletor da aba Reforma Tributária da natureza de operação. Lookup sem permissão de
     * rotina: a natureza fica em Notas Fiscais e a regra em Reforma Tributária.
     */
    public getLookup(pesquisa: string | null, paginaAtual: number, tamanhoPagina: number): Observable<RegraTributariaLookupResponse> {
        let params = new HttpParams()
            .set('paginaAtual', paginaAtual.toString())
            .set('tamanhoPagina', tamanhoPagina.toString());

        if (pesquisa) {
            params = params.set('pesquisa', pesquisa);
        }

        return this.httpClient.get<RegraTributariaLookupResponse>(`${this.apiUrl}/lookup`, {
            params,
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    /** Cria as regras tributárias padrão da empresa; chamado quando a listagem ou a regra padrão devolve SeedPendente. */
    public seed(): Observable<ReturnStruct> {
        return this.httpClient.post<ReturnStruct>(`${this.apiUrl}/seed`, {}, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public alterarStatus(id: number, isAtivo: boolean): Observable<ReturnStruct> {
        return this.httpClient.patch<ReturnStruct>(`${this.apiUrl}/${id}/status`, { isAtivo }, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public excluir(id: number): Observable<ReturnStruct> {
        return this.httpClient.delete<ReturnStruct>(`${this.apiUrl}/${id}`, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public bulkDelete(request: RegraTributariaBulkDeleteRequest): Observable<ReturnStruct> {
        return this.httpClient.post<ReturnStruct>(`${this.apiUrl}/bulk-delete`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public getCompleto(id: number): Observable<RegraTributariaCompletaResponse> {
        return this.httpClient.get<RegraTributariaCompletaResponse>(`${this.apiUrl}/${id}/completo`, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public getPadrao(): Observable<RegraTributariaCompletaResponse> {
        return this.httpClient.get<RegraTributariaCompletaResponse>(`${this.apiUrl}/padrao`, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public criarPadrao(request: RegraTributariaCompletaRequest): Observable<RegraTributariaSalvarResponse> {
        return this.httpClient.post<RegraTributariaSalvarResponse>(`${this.apiUrl}/padrao`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public atualizarPadrao(id: number, request: RegraTributariaCompletaRequest): Observable<RegraTributariaSalvarResponse> {
        return this.httpClient.put<RegraTributariaSalvarResponse>(`${this.apiUrl}/padrao/${id}`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public criarCompleto(request: RegraTributariaCompletaRequest): Observable<RegraTributariaSalvarResponse> {
        return this.httpClient.post<RegraTributariaSalvarResponse>(`${this.apiUrl}/completo`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public atualizarCompleto(id: number, request: RegraTributariaCompletaRequest): Observable<RegraTributariaSalvarResponse> {
        return this.httpClient.put<RegraTributariaSalvarResponse>(`${this.apiUrl}/${id}/completo`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public getNaturezasVinculadas(id: number): Observable<RegraNaturezasVinculadasResponse> {
        return this.httpClient.get<RegraNaturezasVinculadasResponse>(`${this.apiUrl}/${id}/naturezas-vinculadas`, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    /** Aviso antecipado (não bloqueante) de sobreposição de escopo; o bloqueio real acontece no salvar. */
    public validarSobreposicao(request: RegraTributariaCompletaRequest, id: number | null): Observable<RegraTributariaSobreposicaoResponse> {
        let params = new HttpParams();
        if (id != null) {
            params = params.set('id', id.toString());
        }
        return this.httpClient.post<RegraTributariaSobreposicaoResponse>(`${this.apiUrl}/validar-sobreposicao`, request, {
            params,
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }
}
